import { z } from "zod";
import { setError } from "@/store/features/placeOrderSlice";
import { isAlmostDivisible, putComma } from "./utils";
import { getSellQty } from "./placeOrder";

class PlaceOrderValidator {
    constructor(dispatch, state) {
        this.dispatch = dispatch;
        this.state = state;
    }

    validatePrice(price, trigger_price, order_type, uckt, lckt, tickSize = 0.01, type) {
        if (order_type.disablePrice) {
            this.dispatch(setError({ price: null }));
            return true;
        }
        const priceSchema = z
            .string()
            .trim()
            .refine((value) => value === "" || /^\d*\.?\d{0,2}$/.test(value), {
                message: "Price must be a valid number."
            })
            .superRefine((value, ctx) => {
                const temPrice = isNaN(parseFloat(value)) ? 0 : parseFloat(value);
                // if (uckt > 0 && lckt > 0) {
                //     if (temPrice < lckt || temPrice > uckt) {
                //         ctx.addIssue({
                //             code: z.ZodIssueCode.custom,
                //             message: `Price must be between ${putComma(lckt)} and ${putComma(uckt)}.`
                //         });
                //     }
                // }

                if (!isAlmostDivisible(temPrice, tickSize)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Price must be multiple of ${tickSize}.`
                    });
                }
                if (type === "BUY") {
                    if (order_type.code === "SL") {
                        if (price < parseFloat(trigger_price)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Price must be more than trigger price"
                            });
                        }
                    }
                } else {
                    if (order_type.code === "SL") {
                        if (price > parseFloat(trigger_price)) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Price must be smaller than trigger price"
                            });
                        }
                    }
                }
            })
            .transform((value) => (value === "" ? "0" : value));
        const validationResult = priceSchema.safeParse(price);
        if (validationResult.success) {
            this.dispatch(setError({ price: null }));
            return true;
        } else {
            this.dispatch(setError({ price: validationResult.error.issues[0]?.message }));
            return false;
        }
    }

    validateQuantity(q, disc_quantity, type, totalQuantity) {
        const quantity = q?.toString();
        if (disc_quantity * 10 >= quantity) {
            this.dispatch(setError({ disc_quantity: null }));
        }

        const quantitySchema = z
            .string()
            .refine((value) => value !== "", {
                message: "Field can not be blank"
            })
            .refine((value) => /^[1-9]\d*$|^0$/.test(value), {
                message: "Quantity must be a whole number."
            })
            .refine((value) => parseFloat(value) > 0, {
                message: "Field cannot be 0"
            })
            .superRefine((value, ctx) => {
                const tempQuantity = parseFloat(quantity ?? "0");
                const tempTotalQuantity = parseFloat(totalQuantity ?? "0");
                if (type === "SELL" && tempQuantity != 0 && tempQuantity < tempQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Sell quantity must be smaller than ${tempTotalQuantity}.`
                    });
                }
            })
            .transform((value) => (value === "" ? "0" : value));
        const validationResult = quantitySchema.safeParse(quantity);
        if (!validationResult.success) {
            this.dispatch(setError({ quantity: validationResult.error.issues[0]?.message }));
            return false;
        } else {
            this.dispatch(setError({ quantity: null }));
            return true;
        }
    }

    validateTriggerPrice(trigger_price, price, order_type, type, ltp) {
        if (!["SL", "SLM"].includes(order_type.code)) {
            return true;
        }
        var isNoError = true;

        const triggerPriceSchema = z
            .string()
            .trim()
            .refine((value) => value !== "", {
                message: "Field can not be blank"
            })
            .refine((value) => /^\d*\.?\d{0,2}$/.test(value), {
                message: "Trigger price must be a valid number."
            })
            // .refine((value) => (["SL", "SLM"].includes(order_type.code) ? parseFloat(value) > 0 : true), {
            //     message: "Trigger price cannot be 0"
            // })
            .refine((value) => parseFloat(value) > 0, {
                message: "Field cannot be 0"
            })
            .superRefine((value, ctx) => {
                const ltpPrice = parseFloat(ltp ?? "0");
                const temPrice = parseFloat(price ?? "0");
                const parsedValue = parseFloat(value);

                if (type === "BUY") {
                    if (order_type.code === "SL") {
                        if (parsedValue < ltpPrice) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Trigger price must be greater than LTP."
                            });
                        }
                        if (price && parsedValue > parseFloat(price)) {
                            this.dispatch(setError({ price: "Price must be more than trigger price" }));
                            isNoError = false;
                        } else {
                            this.dispatch(setError({ price: null }));
                        }
                    } else if (order_type.code === "SLM") {
                        if (parsedValue <= temPrice) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Trigger price must be greater than LTP."
                            });
                        }
                    }
                } else {
                    if (order_type.code === "SL") {
                        if (parsedValue > ltpPrice) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Trigger price must be smaller than LTP."
                            });
                        }
                        if (price && parsedValue < parseFloat(price)) {
                            this.dispatch(setError({ price: "Price must be less than trigger price" }));
                            isNoError = false;
                        } else {
                            this.dispatch(setError({ price: null }));
                        }
                    } else if (order_type.code === "SLM") {
                        if (parsedValue >= temPrice) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: "Trigger price must be smaller than LTP."
                            });
                        }
                    }
                }
            })
            .transform((value) => (value === "" ? "0" : value));
        const validationResult = triggerPriceSchema.safeParse(trigger_price);
        if (!validationResult.success) {
            this.dispatch(setError({ trigger_price: validationResult.error.issues[0]?.message }));
            isNoError = false;
        } else {
            this.dispatch(setError({ trigger_price: null }));
        }
        return isNoError;
    }

    validateStopLoss(stopLoss, price, type, shouldPlaceGtt = false) {
        if (!shouldPlaceGtt) {
            this.dispatch(setError({ stopLoss: null }));
            return true;
        }
        console.log(stopLoss, price, type, shouldPlaceGtt);
        const stopLossSchema = z
            .string()
            .trim()
            .refine((value) => value === "" || /^\d*\.?\d{0,2}$/.test(value), {
                message: "Stop loss must be a valid number."
            })
            .superRefine((value, ctx) => {
                const temPrice = parseFloat(price);
                const parsedValue = parseFloat(value);
                if (type === "BUY" && parsedValue >= temPrice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Stop loss must be lower than price."
                    });
                } else if (type === "SELL" && parsedValue <= temPrice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Stop loss must be greater than price."
                    });
                }
            });
        const validationResult = stopLossSchema.safeParse(stopLoss);
        if (!validationResult.success) {
            this.dispatch(setError({ stopLoss: validationResult.error.issues[0]?.message }));
            return false;
        } else {
            this.dispatch(setError({ stopLoss: null }));
            return true;
        }
    }

    validateTargetPrice(targetPrice, price, type, shouldPlaceGtt = false) {
        if (!shouldPlaceGtt) {
            this.dispatch(setError({ stopLoss: null }));
            return true;
        }
        const targetPriceSchema = z
            .string()
            .trim()
            .refine((value) => value === "" || /^\d*\.?\d{0,2}$/.test(value), {
                message: "Target price must be a valid number."
            })
            .refine((value) => value === "" || parseFloat(value) > 0, {
                message: "Target price cannot be 0"
            })
            .superRefine((value, ctx) => {
                const temPrice = parseFloat(price);
                const parsedValue = parseFloat(value);
                if (type === "BUY" && parsedValue <= temPrice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Target price must be greater than price."
                    });
                } else if (type === "SELL" && parsedValue >= temPrice) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Target price must be lower than price."
                    });
                }
            });
        const validationResult = targetPriceSchema.safeParse(targetPrice);
        if (!validationResult.success) {
            this.dispatch(setError({ targetPrice: validationResult.error.issues[0]?.message }));
            return false;
        } else {
            this.dispatch(setError({ targetPrice: null }));
            return true;
        }
    }

    validateDiscQuantity(disc_quantity, quantity, disableDisclose = false) {
        if (disableDisclose) {
            this.dispatch(setError({ disc_quantity: null }));
            return true;
        }
        const discQuantitySchema = z
            .string()
            .trim()
            .refine((value) => value === "" || /^\d*\.?\d{0,2}$/.test(value), {
                message: "Disclose qty. must be a valid number."
            })
            .superRefine((value, ctx) => {
                const tempQuantity = parseFloat(quantity);
                const parsedValue = parseFloat(value);
                if (parsedValue > tempQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Disclose qty. must be smaller than quantity"
                    });
                } else if (parsedValue < 0.1 * tempQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: "Disclose qty. must be more than 10% of qty."
                    });
                }
            })
            .transform((value) => (value === "" ? "0" : value));
        const validationResult = discQuantitySchema.safeParse(disc_quantity);
        if (!validationResult.success) {
            this.dispatch(setError({ disc_quantity: validationResult.error.issues[0]?.message }));
            return false;
        } else {
            this.dispatch(setError({ disc_quantity: null }));
            return true;
        }
    }

    validateAll() {
        const {
            price,
            quantity,
            trigger_price,
            stopLoss,
            targetPrice,
            disc_quantity,
            order_type,
            type,
            shouldPlaceGtt,
            uckt,
            lckt,
            ltp,
            orderMargin,
            isNfo,
            isMoreOpen,
            productType
        } = this.state;
        const sellQty = getSellQty(productType, orderMargin);
        const isQuantityValid = this.validateQuantity(quantity, disc_quantity, type, sellQty);
        const isTriggerPriceValid = this.validateTriggerPrice(trigger_price, price, order_type, type, ltp);
        const isPriceValid = this.validatePrice(
            price,
            trigger_price,
            order_type,
            uckt,
            lckt,
            orderMargin?.responseMap?.tickSize,
            type
        );
        const isStopLossValid = this.validateStopLoss(stopLoss, price, type, shouldPlaceGtt);
        const isTargetPriceValid = this.validateTargetPrice(targetPrice, price, type, shouldPlaceGtt);
        const isDiscQuantityValid = this.validateDiscQuantity(disc_quantity, quantity, isNfo || !isMoreOpen);

        return (
            isPriceValid &&
            isQuantityValid &&
            isTriggerPriceValid &&
            isStopLossValid &&
            isTargetPriceValid &&
            isDiscQuantityValid
        );
    }
}

export default PlaceOrderValidator;
